import { CreativeType } from 'core/creative/Creative';
import { L2ObjectOptimizationGoal } from 'core/l2Object/L2Object';
import { LimitationData } from 'core/limitation/Limitation';
import { Product } from 'core/product/Product';

export type RtbCampaign = {
  basic: RtbCampaignBasic;
  limitations: LimitationData;
  products?: Product[];
};

export type RtbCampaignBasic = {
  id?: number;
  name: string;
  groupId: number;
  advertiserId: number;
  orderId: number;
  budget: number;
  startDate: string;
  endDate: string;
  createDate?: string;
  priceModel: RtbCampaignPlanType;
  orderPrice?: number;
  optimize: L2ObjectOptimizationGoal;
  bidPrice?: number;
  creativeDeliverType: CreativeDeliverType;
  deliverType: DeliverType;
  state?: CampaignState;
  effectiveStatus?: string;
  spents?: number;
  currencyRate?: number;
  olapActualSpent?: number;
  olapExpectSpent?: number;
  expectedSpent: number;
  impres?: number;
  clicks?: number;
  dailyTargetBudget: number | null;
  frequency?: CampaignFrequency;
  dayPart?: { [key: string]: string[] | number[] | string };
  checkpoints: CheckPoint[];
  dealIds?: string[];
  adType: AdType;
  additionalInfo?: AdditionalInfo;
  tags: Array<string>;
  bindings?: {
    bindingId: string,
    active: boolean
  },
  report?: any;
  ecommerceReport?: any;
  ageMin: number;
  ageMax: number;
};

export type RtbCampaignListBasic = Omit<
  RtbCampaignBasic,
  'orderPriceEnable'
> & {
  effectiveStatus: string;
  viewable: number;
  convs: number;
  adView: number;
  magnificationRatio: number;
  l2ObjectId: number;
  additionalInfo: AdditionalInfo;
  uuCount: number;
  report?: {
    results: number;
  };
  bindings?: {
    bindingId: string;
    active: boolean;
  };
  adminBidPrice?: number;
};

export type AdditionalInfo = {
  creativeAmount: CreativeAmount,
  limitations: { [key: string]: object[] }
};

export type CreativeAmount = {
  bindingCount: number;
  enableCount: number;
  enableParentCount: number;
};

export type CampaignFrequency = {
  maxFrequency: number;
  intervalDays: number;
};

export type CheckPoint = {
  target: number;
  time: string;
  utime: string;
};

export type RecommendBidPrice = {
  bidCap: number;
  bidFloor: number;
};

export type CampaignAnalytics = {
  bidPrice?: number;
  lifetimeBudgetRank?: number;
  dailyBudgetRank?: number;
  brandAwarenessGrowth?: number;
  trafficGrowth?: number;
  keywordSearchVolume?: number;
};

export enum AdType {
  DISPLAY = 'DISPLAY',
  KEYWORD = 'KEYWORD',
  PMP = 'PMP',
  WEB_DISPLAY = 'WEB_DISPLAY',
  UNKNOWN = 'UNKNOWN',
  BRAND_LED = 'BRAND_LED',
  SITE_WIDE_RECOMMENDED = 'SITE_WIDE_RECOMMENDED',
  PERSONALIZED_GOLDEN_RECOMMENDED = 'PERSONALIZED_GOLDEN_RECOMMENDED',
  RICH_MEDIA = 'RICH_MEDIA'
}

export const PRODUCT_AD_TYPES: AdType[] = [
  AdType.DISPLAY,
  AdType.WEB_DISPLAY,
  AdType.KEYWORD,
  AdType.BRAND_LED,
  AdType.SITE_WIDE_RECOMMENDED,
  AdType.PERSONALIZED_GOLDEN_RECOMMENDED,
  AdType.RICH_MEDIA
];

export enum RtbOptimize {
  IMPRESSIONS = 'IMPRESSIONS',
  CLICKS = 'CLICKS'
}

export enum RtbCampaignPlanType {
  RS = 'rs',
  FCPC = 'fcpc',
  FCPM = 'fcpm',
  RS_CPC = 'rsCpc',
  RS_CPM = 'rsCpm'
}

export enum CreativeDeliverType {
  OPTIMIZE = 1,
  AVERAGE = 2
}

export enum CampaignState {
  ACTIVATE = 1,
  DEACTIVATE = 2,
  DELETE = 3,
  STOPPING = 4
}

export enum DailyBudgetPlan {
  SCHEDULE,
  DAILY
}

export enum DeliverType {
  STANDARD = 1,
  ACCELERATED = 2
}

export const AD_TYPE_MAP_CREATIVE_TYPE = {
  [AdType.DISPLAY]: [CreativeType.IMAGE, CreativeType.RICH_PRODUCT_NATIVE],
  [AdType.WEB_DISPLAY]: [CreativeType.WEB_RICH_PRODUCT_NATIVE],
  [AdType.KEYWORD]: [CreativeType.PRODUCT_NATIVE],
  [AdType.PMP]: [CreativeType.IMAGE],
  [AdType.BRAND_LED]: [CreativeType.BRAND_LED],
  [AdType.SITE_WIDE_RECOMMENDED]: [CreativeType.SITE_WIDE_RECOMMENDED],
  [AdType.PERSONALIZED_GOLDEN_RECOMMENDED]: [CreativeType.PERSONALIZED_GOLDEN_RECOMMENDED],
  [AdType.RICH_MEDIA]: [CreativeType.RICH_MEDIA]
};

export const RTBCAMPAIGN_DEFAULT_AGE_MIN = 13;
export const RTBCAMPAIGN_DEFAULT_AGE_MAX = 65;

export enum Gender {
  ALL = -1,
  MALE = 1,
  FEMALE
}

export enum LegacyOptimize {
  CPM = 'CPM',
  CPC = 'CPC'
}

export const legacyOptimizeMap = {
  [L2ObjectOptimizationGoal.IMPRESSIONS]: LegacyOptimize.CPM,
  [L2ObjectOptimizationGoal.CLICKS]: LegacyOptimize.CPC
};
